import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@mlm/ui';
import {
  activateFsSlot,
  apiErrorMessage,
  listFsMemberships,
  searchMembers,
  type ActivateFsResult,
  type MemberSearchResult,
} from '../../lib/api';

/**
 * FS Activation via Cashier — directly create + activate a FREE (FS) slot for a
 * member (no order/payment). Self-contained: its own member search + FS-eligible
 * tier picker. Only tiers an admin flagged FS-eligible are listed; uplines earn
 * nothing on a free entry.
 */
// Server codes worth a human sentence at the counter (others show as-is).
const FS_ERROR_COPY: Record<string, string> = {
  recruit_membership_not_allowed:
    "The sponsor's membership is not allowed to recruit this package.",
  membership_binary_locked:
    'This package cannot take a binary position — clear the anchor/side and try again.',
};

export function FreeSlotDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [term, setTerm] = useState('');
  const [member, setMember] = useState<MemberSearchResult | null>(null);
  const [membershipId, setMembershipId] = useState('');
  const [sponsorCode, setSponsorCode] = useState('');
  const [result, setResult] = useState<ActivateFsResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trimmed = term.trim();
  const search = useQuery({
    queryKey: ['fs-members-search', trimmed],
    queryFn: () => searchMembers(trimmed),
    enabled: open && trimmed.length >= 2 && !member,
    staleTime: 5_000,
  });
  const memberships = useQuery({
    queryKey: ['fs-memberships'],
    queryFn: listFsMemberships,
    enabled: open,
  });

  const activate = useMutation({
    mutationFn: activateFsSlot,
    onSuccess: (r) => {
      setResult(r);
      setError(null);
    },
    onError: (e) => {
      const msg = apiErrorMessage(e, 'Activation failed');
      setError(FS_ERROR_COPY[msg] ?? msg);
    },
  });

  function reset() {
    setTerm('');
    setMember(null);
    setMembershipId('');
    setSponsorCode('');
    setResult(null);
    setError(null);
    activate.reset();
  }
  function close() {
    reset();
    onClose();
  }

  // De-dupe search rows (smart search returns user + slot matches for one person).
  const memberResults = Array.from(new Map((search.data ?? []).map((r) => [r.id, r])).values());

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Activate free (FS) slot</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-emerald-900">
              Free slot <span className="font-mono font-semibold">{result.slotCode}</span> created
              and activated{result.membershipCode ? ` (${result.membershipCode})` : ''}.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={reset}>
                Activate another
              </Button>
              <Button onClick={close}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">
              Creates a free (FS) slot for a member — no order or payment. Only FS-eligible tiers
              are listed. Uplines earn nothing on a free entry.
            </p>

            {member ? (
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <div className="font-medium">{member.fullName ?? member.email}</div>
                  <div className="text-xs text-muted-foreground">{member.email}</div>
                </div>
                <button className="text-xs underline" onClick={() => setMember(null)}>
                  Change
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Member</label>
                <Input
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="Search name / email / phone…"
                />
                {trimmed.length >= 2 && (
                  <div className="max-h-40 overflow-auto rounded-md border divide-y">
                    {search.isLoading ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
                    ) : memberResults.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">No matches.</div>
                    ) : (
                      memberResults.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => {
                            setMember(r);
                            setTerm('');
                          }}
                          className="block w-full text-left px-3 py-2 hover:bg-accent"
                        >
                          <div className="font-medium">{r.fullName ?? r.email}</div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">FS-eligible tier</label>
              <select
                className="w-full h-9 rounded-md border bg-background px-2 text-sm"
                value={membershipId}
                onChange={(e) => setMembershipId(e.target.value)}
              >
                <option value="">Select a tier…</option>
                {(memberships.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.code})
                  </option>
                ))}
              </select>
              {memberships.data && memberships.data.length === 0 && (
                <p className="text-[10px] text-warning">
                  No FS-eligible tiers. Mark a membership FS-eligible in Admin → Memberships.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Sponsor (optional)</label>
              <Input
                value={sponsorCode}
                onChange={(e) => setSponsorCode(e.target.value)}
                placeholder="Referral or slot code — blank = default"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                disabled={!member || !membershipId || activate.isPending}
                onClick={() => {
                  setError(null);
                  activate.mutate({
                    memberId: member!.id,
                    membershipId,
                    sponsorCode: sponsorCode.trim() || undefined,
                  });
                }}
              >
                {activate.isPending ? 'Activating…' : 'Activate free slot'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
